'use client';
import { AdvancedMatchesFeedWidget } from '@/components/AdvancedMatchesFeedWidget';
import { BlogCategoryIcon } from '@/components/BlogCategoryIcon';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/button';
import { BlogCard } from '@/components/cards/blog/BlogCard';
import { FeaturedBlogCard } from '@/components/cards/blog/FeaturedBlogCard';
import { InfiniteCarousel } from '@/components/carousels/infinite';
import { EmptyState } from '@/components/EmptyState';
import { SplitPageLayout } from '@/components/layout/SplitPageLayout';
import { MainPageTitle } from '@/components/MainPageTitle';
import { PageSectionTitle } from '@/components/PageSectionTitle';
import { Pagination } from '@/components/pagination';
import { BlogCategory } from '@/constants/blog.const';
import { usePaginationNavigation } from '@/hooks/usePaginationNavigation.hook';
import { DEFAULT_BLOGS_PER_PAGE } from '@/strapi/blog.util';
import type { Blog } from '@/types/blog';
import { getStrapiBlogCategoryFromBlogCategory } from '@/utils/blogCategoryMappers';
import { cn } from '@/utils/cn';
import { getBlogPath } from '@/utils/paths';
import { sortStrapiItemsDesc } from '@/utils/strapi';

interface BlogPageProps {
  blogItems: {
    data: Blog[];
    meta: { pagination: { pageCount: number; total: number } };
  };
  featuredBlogItems: { data: Blog[] };
  currentPage: number;
  selectedBlogCategory?: BlogCategory;
}

export const BlogPage = ({
  blogItems,
  featuredBlogItems,
  currentPage,
  selectedBlogCategory,
}: BlogPageProps) => {
  const { handlePageChange } = usePaginationNavigation({
    scrollTargetElementId: 'blogs',
  });

  const sortedFeaturedBlogItems = sortStrapiItemsDesc(featuredBlogItems.data);

  return (
    <>
      <Breadcrumbs breadcrumbs={[{ title: 'Blog' }]} className="mb-5" />

      <MainPageTitle>Blog</MainPageTitle>

      <BlogCategorySelector
        className="mb-4"
        selectedBlogCategory={selectedBlogCategory ?? null}
      />

      {sortedFeaturedBlogItems.length > 0 && (
        <div className="mb-[30px] flex flex-col items-center gap-5 md:mb-[60px]">
          <div className="hidden w-full md:block">
            <InfiniteCarousel slideWidthPercentage={45}>
              {sortedFeaturedBlogItems.map((item, index: number) => (
                <FeaturedBlogCard
                  data={item}
                  key={`featured-${item.id || index}`}
                />
              ))}
            </InfiniteCarousel>
          </div>
          <div className="flex flex-col gap-4 md:hidden">
            {sortedFeaturedBlogItems.map((item, index: number) => (
              <FeaturedBlogCard
                data={item}
                key={`featured-mobile-${item.id || index}`}
              />
            ))}
          </div>
        </div>
      )}

      <PageSectionTitle className="mb-5" id="blogs">
        All blog posts
      </PageSectionTitle>

      <SplitPageLayout>
        {/* COL 1 */}
        <div className="flex flex-col gap-4 rounded-lg">
          {blogItems.data.length === 0 && (
            <div className="flex items-center justify-center gap-2 rounded-lg bg-card-bg px-3 py-5">
              <EmptyState
                description="We couldn't find any blog posts right now. Please check back later."
                title="No blog posts found"
              />
            </div>
          )}
          <div className="flex flex-col gap-3">
            {blogItems.data.map((article) => (
              <BlogCard
                blog={article}
                className="rounded-xl bg-medium-gray p-4"
                key={article.id}
              />
            ))}
          </div>
          <Pagination
            currentPage={currentPage}
            onChange={handlePageChange}
            perPageItems={DEFAULT_BLOGS_PER_PAGE}
            // TODO: don't hard code this number
            totalCount={blogItems.meta.pagination.total}
            totalPages={blogItems.meta.pagination.pageCount}
          />
        </div>
        {/* COL 2 */}
        <div className="flex flex-col gap-5">
          <AdvancedMatchesFeedWidget type="finished" />
          <AdvancedMatchesFeedWidget type="not_started" />
        </div>
      </SplitPageLayout>
    </>
  );
};

function BlogCategorySelector({
  selectedBlogCategory,
  className,
}: {
  selectedBlogCategory: BlogCategory | null;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-row',
        'max-w-full overflow-x-auto',
        'scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-200',
        'scrollbar-thumb-rounded-full scrollbar-track-rounded-lg',
        className
      )}
    >
      <div className="flex items-center space-x-4 rounded-lg bg-medium-gray px-4 py-2">
        <Button
          aria-current={selectedBlogCategory === null ? 'page' : undefined}
          as="link"
          href={getBlogPath()}
          size="sm"
          variant={selectedBlogCategory === null ? 'default' : 'ghost'}
        >
          All
        </Button>

        {Object.values(BlogCategory).map((blogCategory) => (
          <Button
            aria-current={
              selectedBlogCategory === blogCategory ? 'page' : undefined
            }
            as="link"
            href={getBlogPath({ blogCategory })}
            key={blogCategory}
            size="sm"
            variant={
              selectedBlogCategory === blogCategory ? 'default' : 'ghost'
            }
          >
            <BlogCategoryIcon
              aria-hidden="true"
              blogCategory={blogCategory}
              className="w-4 text-white"
            />
            {getStrapiBlogCategoryFromBlogCategory(blogCategory)}
          </Button>
        ))}
      </div>
    </div>
  );
}
